import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Phone, Video, X, PhoneCall, PhoneOff, Mic, MicOff, Camera, CameraOff, RefreshCcw, Maximize, Minimize } from 'lucide-react';

const ICE_SERVERS = {
    iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        { urls: 'stun:stun2.l.google.com:19302' },
        {
            urls: [
                "turn:152.53.109.82:3478?transport=udp",
                "turn:152.53.109.82:3478?transport=tcp",
                "turn:api.luxvono.com:3478?transport=udp",
                "turn:api.luxvono.com:3478?transport=tcp"
            ],
            username: "luxvono",
            credential: "luxvono2026"
        }
    ]
};

const WebRTCCallOverlay = ({
    sessionId,
    customerId,
    customerName,
    stompClient,
    currentStatus, // idle, receiving, calling, connected
    setCurrentStatus,
    callType,
    setCallType,
    remoteName,
    setRemoteName
}) => {
    const localVideoRef = useRef(null);
    const remoteVideoRef = useRef(null);
    const remoteAudioRef = useRef(null);
    const pcRef = useRef(null);
    const localStreamRef = useRef(null);
    const iceQueueRef = useRef([]);

    const [activeCallSessionId, setActiveCallSessionId] = useState(sessionId);
    const activeCallSessionIdRef = useRef(sessionId);
    const activeCallRecipientIdRef = useRef(null);
    const [instanceId] = useState(() => 'customer_' + Math.random().toString(36).substring(2, 15));
    const [remoteAvatarUrl, setRemoteAvatarUrl] = useState(null);
    const [callSeconds, setCallSeconds] = useState(0);

    useEffect(() => {
        let timer;
        if (currentStatus === 'connected') {
            timer = setInterval(() => {
                setCallSeconds(prev => prev + 1);
            }, 1000);
        } else {
            setCallSeconds(0);
        }
        return () => {
            if (timer) clearInterval(timer);
        };
    }, [currentStatus]);

    const formatDuration = (sec) => {
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    useEffect(() => {
        if (sessionId) {
            setActiveCallSessionId(sessionId);
            activeCallSessionIdRef.current = sessionId;
        }
    }, [sessionId]);

    const resolveAvatarUrl = (url) => {
        if (!url) return null;
        if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
            return url;
        }
        const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:8080' : 'https://api.luxvono.com';
        if (url.startsWith('/')) {
            return `${apiBase}${url}`;
        }
        return `${apiBase}/${url}`;
    };

    // Dedicated session subscription for ultra-fast, isolated P2P signaling
    useEffect(() => {
        const targetSid = Number(activeCallSessionId || sessionId);
        const client = stompClient || window.__LUXVONO_STOMP_CLIENT;
        if (!client || (!client.connected && !client.active)) {
            return;
        }

        let sessionSub = null;
        let customerSub = null;

        try {
            if (targetSid && typeof client.subscribe === 'function') {
                sessionSub = client.subscribe(`/topic/chat/${targetSid}/signal`, (msg) => {
                    try {
                        if (msg.body) {
                            const signal = JSON.parse(msg.body);
                            window.dispatchEvent(new CustomEvent('webrtc-signal', { detail: signal }));
                        }
                    } catch (e) {
                        console.warn('Failed to parse session signal:', e);
                    }
                });
            }

            if (customerId && typeof client.subscribe === 'function') {
                customerSub = client.subscribe(`/topic/customer/${customerId}/signal`, (msg) => {
                    try {
                        if (msg.body) {
                            const signal = JSON.parse(msg.body);
                            window.dispatchEvent(new CustomEvent('webrtc-signal', { detail: signal }));
                        }
                    } catch (e) {
                        console.warn('Failed to parse customer signal:', e);
                    }
                });
            }
        } catch (err) {
            console.warn('Storefront session signal subscription error:', err);
        }

        return () => {
            if (sessionSub && typeof sessionSub.unsubscribe === 'function') {
                try { sessionSub.unsubscribe(); } catch (e) {}
            }
            if (customerSub && typeof customerSub.unsubscribe === 'function') {
                try { customerSub.unsubscribe(); } catch (e) {}
            }
        };
    }, [stompClient, activeCallSessionId, sessionId, customerId]);

    const [isMuted, setIsMuted] = useState(false);
    const [isVideoOff, setIsVideoOff] = useState(false);
    const [facingMode, setFacingMode] = useState('user'); // 'user' or 'environment'
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Vibrate during incoming call
    useEffect(() => {
        let vibrateInterval;
        if (currentStatus === 'receiving' && window.navigator?.vibrate) {
            try {
                window.navigator.vibrate([300, 200, 300, 200, 500]);
                vibrateInterval = setInterval(() => {
                    window.navigator.vibrate([300, 200, 300, 200, 500]);
                }, 2000);
            } catch (_) {}
        }
        return () => {
            if (vibrateInterval) clearInterval(vibrateInterval);
        };
    }, [currentStatus]);

    const sendSignal = useCallback((type, payload = null, overrideCallType = null, overrideSessionId = null, retryCount = 0) => {
        const targetSid = Number(overrideSessionId || activeCallSessionIdRef.current || activeCallSessionId || sessionId);
        const client = stompClient || window.__LUXVONO_STOMP_CLIENT;
        if (!client || (!client.connected && !client.active) || !targetSid) {
            console.warn('[WebRTC] Cannot send signal immediately, waiting to retry...', { type, targetSid, retryCount });
            if (retryCount < 5 && targetSid) {
                setTimeout(() => {
                    sendSignal(type, payload, overrideCallType, targetSid, retryCount + 1);
                }, 800);
            }
            return;
        }

        const destination = '/app/chat.signal';
        const numericSenderId = customerId && !isNaN(Number(customerId)) ? Number(customerId) : instanceId;
        const payloadObj = payload && typeof payload === 'object' ? (typeof payload.toJSON === 'function' ? payload.toJSON() : payload) : payload;
        const body = JSON.stringify({
            sessionId: targetSid,
            senderId: numericSenderId,
            senderClientId: instanceId,
            senderName: customerName || 'Customer',
            recipientId: activeCallRecipientIdRef.current || null,
            type,
            callType: overrideCallType || callType,
            payload: payloadObj,
            sdp: payloadObj?.sdp || null,
            candidate: payloadObj?.candidate || null,
            sdpMid: payloadObj?.sdpMid ?? null,
            sdpMLineIndex: payloadObj?.sdpMLineIndex ?? null
        });

        try {
            if (typeof client.publish === 'function') {
                client.publish({ destination, body, headers: { 'content-type': 'application/json' } });
            } else if (typeof client.send === 'function') {
                client.send(destination, { 'content-type': 'application/json' }, body);
            }
        } catch (err) {
            console.error('[WebRTC] sendSignal caught error:', err);
        }
    }, [stompClient, activeCallSessionId, sessionId, customerId, callType, instanceId]);

    useEffect(() => {
        const handleIncomingSignal = async (e) => {
            const signal = e.detail;
            if (!signal) return;
            if (signal.senderClientId === instanceId) return; // ignore our own client instance
            if (customerId && String(signal.senderId) === String(customerId)) return; // ignore our own signals
            
            const currentSid = Number(activeCallSessionIdRef.current || activeCallSessionId || sessionId);
            const incomingSid = Number(signal.sessionId);
            const normalizedType = String(signal.type || '').trim().toUpperCase();

            switch (normalizedType) {
                case 'CALL_REQUEST':
                    if (currentStatus !== 'idle' && currentStatus !== 'receiving') return;
                    if (incomingSid) {
                        setActiveCallSessionId(incomingSid);
                        activeCallSessionIdRef.current = incomingSid;
                    }
                    if (signal.senderId) {
                        activeCallRecipientIdRef.current = signal.senderId;
                    }
                    setCallType(signal.callType || 'video');
                    setRemoteName(signal.senderName || 'Support Agent');
                    setRemoteAvatarUrl(signal.senderAvatarUrl || null);
                    setCurrentStatus('receiving');
                    break;
                case 'CALL_ACCEPTED':
                    if (incomingSid) {
                        setActiveCallSessionId(incomingSid);
                        activeCallSessionIdRef.current = incomingSid;
                    }
                    if (currentStatus === 'calling' || currentStatus === 'idle') {
                        setCurrentStatus('connected');
                        await createOffer();
                    } else if (currentStatus === 'receiving') {
                        cleanupCall();
                    }
                    break;
                case 'CALL_REJECTED':
                case 'REJECT_CALL':
                case 'CALL_DECLINED':
                    cleanupCall();
                    if (signal.reason === 'FRIENDS_ONLY' || signal.message) {
                        alert(signal.message || 'المكالمات الصوتية والمرئية متاحة فقط للأصدقاء المشتركين.');
                    }
                    break;
                case 'END_CALL':
                case 'CALL_ENDED':
                case 'CANCEL':
                case 'CALL_CANCEL':
                    cleanupCall();
                    break;
                case 'OFFER': {
                    if (incomingSid) {
                        setActiveCallSessionId(incomingSid);
                        activeCallSessionIdRef.current = incomingSid;
                    }
                    setCurrentStatus('connected');
                    const offerPayload = signal.payload || (signal.sdp ? { type: 'offer', sdp: signal.sdp } : null);
                    if (offerPayload) await handleOffer(offerPayload);
                    break;
                }
                case 'ANSWER': {
                    const answerPayload = signal.payload || (signal.sdp ? { type: 'answer', sdp: signal.sdp } : null);
                    if (answerPayload) await handleAnswer(answerPayload);
                    break;
                }
                case 'ICE_CANDIDATE':
                case 'CANDIDATE': {
                    const candPayload = signal.payload || (signal.candidate ? {
                        candidate: signal.candidate,
                        sdpMid: signal.sdpMid ?? '0',
                        sdpMLineIndex: signal.sdpMLineIndex ?? 0
                    } : null);
                    if (candPayload) await handleIceCandidate(candPayload);
                    break;
                }
                default:
                    break;
            }
        };

        window.addEventListener('webrtc-signal', handleIncomingSignal);
        return () => window.removeEventListener('webrtc-signal', handleIncomingSignal);
    }, [customerId, currentStatus, activeCallSessionId, sessionId, instanceId, setCallType, setRemoteName, setCurrentStatus]);

    const getMedia = async (videoEnabled) => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: videoEnabled ? { facingMode: facingMode } : false,
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                }
            });
            localStreamRef.current = stream;
            if (localVideoRef.current) {
                localVideoRef.current.srcObject = stream;
            }
            return stream;
        } catch (err) {
            console.error('Failed to get local media', err);
            throw err;
        }
    };

    const setupPeerConnection = (stream) => {
        const pc = new RTCPeerConnection(ICE_SERVERS);
        pcRef.current = pc;

        pc.onicecandidate = (event) => {
            if (event.candidate) {
                sendSignal('ICE_CANDIDATE', event.candidate);
            }
        };

        pc.oniceconnectionstatechange = () => {
            console.log('ICE Connection State:', pc.iceConnectionState);
            if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'closed') {
                console.warn('WebRTC P2P Connection Closed/Failed, cleaning up call session');
                cleanupCall();
            }
        };

        pc.ontrack = (event) => {
            if (event.streams && event.streams[0]) {
                const remoteStream = event.streams[0];
                if (remoteVideoRef.current && remoteVideoRef.current.srcObject !== remoteStream) {
                    remoteVideoRef.current.srcObject = remoteStream;
                }
                if (remoteAudioRef.current && remoteAudioRef.current.srcObject !== remoteStream) {
                    remoteAudioRef.current.srcObject = remoteStream;
                }
                
                setTimeout(() => {
                    if (remoteAudioRef.current) {
                        remoteAudioRef.current.muted = false;
                        remoteAudioRef.current.play().catch(e => console.warn('Audio play error:', e));
                    }
                    if (remoteVideoRef.current && remoteVideoRef.current.paused) {
                        remoteVideoRef.current.play().catch(e => console.warn('Video play error:', e));
                    }
                }, 100);
            }
        };

        stream.getTracks().forEach((track) => {
            pc.addTrack(track, stream);
        });

        return pc;
    };

    const startCall = async (type, targetSessionId = null) => {
        if (pcRef.current || localStreamRef.current) return;
        // Unlock Safari AutoPlay by playing a silent base64 audio strictly upon user gesture
        const unlockAudio = new Audio("data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA");
        unlockAudio.play().catch(() => { });

        const sid = Number(targetSessionId || activeCallSessionIdRef.current || activeCallSessionId || sessionId);
        if (!sid) {
            console.warn('[WebRTC] Cannot start call: No valid sessionId found');
            return;
        }
        activeCallSessionIdRef.current = sid;
        setActiveCallSessionId(sid);
        setCallType(type);
        setCurrentStatus('calling');

        // Direct proactive subscription right before sending signal to prevent race condition
        const client = stompClient || window.__LUXVONO_STOMP_CLIENT;
        if (client && (client.connected || client.active)) {
            try {
                client.subscribe(`/topic/chat/${sid}/signal`, (msg) => {
                    try {
                        if (msg.body) {
                            const sig = JSON.parse(msg.body);
                            window.dispatchEvent(new CustomEvent('webrtc-signal', { detail: sig }));
                        }
                    } catch (e) {}
                });
            } catch (_) {}
        }

        try {
            // Get Media immediately upon user interaction to prevent iOS Safari permissions block
            const stream = await getMedia(type === 'video');
            localStreamRef.current = stream;
        } catch (err) {
            console.error('[WebRTC] Media permission error:', err);
            alert(type === 'video' ? "يرجى منح الإذن للكاميرا والميكروفون لبدء المكالمة." : "يرجى منح الإذن للميكروفون لبدء المكالمة.");
            cleanupCall();
            return;
        }

        sendSignal('CALL_REQUEST', null, type, sid);
    };

    const acceptCall = async () => {
        if (currentStatus === 'connected' || pcRef.current) return;
        try {
            // Unlock Safari AutoPlay by playing a silent base64 audio strictly upon user gesture
            const unlockAudio = new Audio("data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA");
            unlockAudio.play().catch(() => { });

            const stream = await getMedia(callType === 'video');
            localStreamRef.current = stream;
            setupPeerConnection(stream);
            setCurrentStatus('connected');
            const targetSid = Number(activeCallSessionIdRef.current || activeCallSessionId || sessionId);
            sendSignal('CALL_ACCEPTED', null, callType, targetSid);
        } catch (err) {
            console.error('[WebRTC] acceptCall permission error:', err);
            alert("يرجى منح إذن استخدام الميكروفون/الكاميرا لقبول المكالمة");
            cleanupCall();
        }
    };

    const rejectCall = () => {
        const targetSid = Number(activeCallSessionIdRef.current || activeCallSessionId || sessionId);
        sendSignal('CALL_REJECTED', null, null, targetSid);
        cleanupCall();
    };

    const handleOffer = async (offerParams) => {
        try {
            if (!pcRef.current) {
                const stream = await getMedia(callType === 'video');
                setupPeerConnection(stream);
                setCurrentStatus('connected');
            }
            const pc = pcRef.current;
            if (pc.signalingState !== 'stable' && pc.signalingState !== 'have-local-offer') {
                return;
            }
            await pc.setRemoteDescription(new RTCSessionDescription(offerParams));
            if (iceQueueRef.current && iceQueueRef.current.length > 0) {
                for (const candidateParams of iceQueueRef.current) {
                    await pc.addIceCandidate(new RTCIceCandidate(candidateParams)).catch(e => {});
                }
                iceQueueRef.current = [];
            }
            if (pc.signalingState !== 'have-remote-offer') return;
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            sendSignal('ANSWER', pc.localDescription);
        } catch (e) {
            console.error(e);
        }
    };

    const handleAnswer = async (answerParams) => {
        try {
            if (pcRef.current && pcRef.current.signalingState === 'have-local-offer') {
                await pcRef.current.setRemoteDescription(new RTCSessionDescription(answerParams));
                if (iceQueueRef.current && iceQueueRef.current.length > 0) {
                    for (const candidateParams of iceQueueRef.current) {
                        await pcRef.current.addIceCandidate(new RTCIceCandidate(candidateParams)).catch(e => {});
                    }
                    iceQueueRef.current = [];
                }
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleIceCandidate = async (candidateParams) => {
        if (pcRef.current && pcRef.current.remoteDescription) {
            try {
                await pcRef.current.addIceCandidate(new RTCIceCandidate(candidateParams));
            } catch (e) {
                console.error('Error adding received ice candidate', e);
            }
        } else {
            // Buffer candidate if remoteDescription is not yet set
            iceQueueRef.current.push(candidateParams);
        }
    };

    const createOffer = async () => {
        if (pcRef.current) return;
        try {
            // Stream should already be fetched in startCall, but fetch if missing
            const stream = localStreamRef.current || await getMedia(callType === 'video');
            if (!localStreamRef.current) localStreamRef.current = stream;
            const pc = setupPeerConnection(stream);
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            sendSignal('OFFER', pc.localDescription);
        } catch (err) {
            console.error(err);
            cleanupCall();
        }
    };

    const cleanupCall = () => {
        setCurrentStatus('idle');
        activeCallRecipientIdRef.current = null;
        if (pcRef.current) {
            pcRef.current.close();
            pcRef.current = null;
        }
        if (localStreamRef.current) {
            const tracks = localStreamRef.current.getTracks();
            setTimeout(() => {
                tracks.forEach(track => {
                    try { track.stop() } catch (e) { }
                });
            }, 100);
            localStreamRef.current = null;
        }
        try {
            if (localVideoRef.current) localVideoRef.current.srcObject = null;
            if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
            if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
        } catch (e) {
            console.warn("React removed video nodes before stream detachment.", e);
        }
        iceQueueRef.current = [];
        setIsMuted(false);
        setIsVideoOff(false);
        setRemoteAvatarUrl(null);
    };

    const toggleMute = () => {
        if (localStreamRef.current) {
            const audioTrack = localStreamRef.current.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.enabled = !audioTrack.enabled;
                setIsMuted(!audioTrack.enabled);
            }
        }
    };

    const toggleVideo = () => {
        if (localStreamRef.current) {
            const videoTrack = localStreamRef.current.getVideoTracks()[0];
            if (videoTrack) {
                videoTrack.enabled = !videoTrack.enabled;
                setIsVideoOff(!videoTrack.enabled);
            }
        }
    };

    const switchCamera = async () => {
        if (callType !== 'video') return;
        const newMode = facingMode === 'user' ? 'environment' : 'user';
        setFacingMode(newMode);

        try {
            // Stop old video track
            if (localStreamRef.current) {
                const oldTrack = localStreamRef.current.getVideoTracks()[0];
                if (oldTrack) oldTrack.stop();
            }

            // Get new video track
            const newStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: newMode }
            });
            const newVideoTrack = newStream.getVideoTracks()[0];

            // Apply new track to current local stream and peer connection
            if (localStreamRef.current) {
                localStreamRef.current.removeTrack(localStreamRef.current.getVideoTracks()[0]);
                localStreamRef.current.addTrack(newVideoTrack);
            }

            if (localVideoRef.current) {
                localVideoRef.current.srcObject = localStreamRef.current;
            }

            if (pcRef.current) {
                const sender = pcRef.current.getSenders().find(s => s.track?.kind === 'video');
                if (sender) {
                    sender.replaceTrack(newVideoTrack);
                }
            }
        } catch (err) {
            console.error('Failed to switch camera', err);
        }
    };

    const startCallRef = useRef(startCall);
    useEffect(() => {
        startCallRef.current = startCall;
    });

    useEffect(() => {
        const handleStartOutgoing = (e) => {
            if (e.detail?.recipientId) {
                activeCallRecipientIdRef.current = e.detail.recipientId;
            }
            if (e.detail?.remoteName) {
                setRemoteName(e.detail.remoteName);
            }
            if (startCallRef.current) {
                startCallRef.current(e.detail.type, e.detail.targetSessionId);
            }
        };
        window.addEventListener('START_OUTGOING_CALL', handleStartOutgoing);
        return () => window.removeEventListener('START_OUTGOING_CALL', handleStartOutgoing);
    }, [setRemoteName]);

    // Return a persistent wrapper instead of null to prevent 'removeChild' crash
    return (
        <div style={{ display: currentStatus === 'idle' ? 'none' : 'block' }}>
            <div className="webrtc-overlay" style={styles.overlay}>
                <div 
                    className="webrtc-modal" 
                    style={isFullscreen 
                        ? { ...styles.modal, width: '100vw', height: '100vh', maxWidth: 'none', borderRadius: 0, border: 'none' } 
                        : styles.modal
                    }
                >
                    <style>
                        {`
                        @keyframes webrtcPulse {
                            0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.5); }
                            70% { box-shadow: 0 0 0 20px rgba(59, 130, 246, 0); }
                            100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
                        }
                        @keyframes statusBlink {
                            0%, 100% { opacity: 1; transform: scale(1); }
                            50% { opacity: 0.4; transform: scale(0.85); }
                        }
                        .webrtc-modal * {
                            box-sizing: border-box;
                        }
                        .webrtc-ctrl-btn {
                            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                        }
                        .webrtc-ctrl-btn:hover {
                            transform: scale(1.08);
                            filter: brightness(1.15);
                        }
                        .webrtc-ctrl-btn:active {
                            transform: scale(0.95);
                        }
                    `}
                    </style>

                    {/* Header */}
                    <div style={styles.header}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: currentStatus === 'connected' ? '#10B981' : (currentStatus === 'calling' ? '#3B82F6' : '#F59E0B'),
                                boxShadow: currentStatus === 'connected' ? '0 0 10px #10B981' : '0 0 10px #3B82F6',
                                animation: 'statusBlink 2s infinite ease-in-out'
                            }} />
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={styles.title}>
                                    {remoteName || 'الدعم الفني'}
                                </span>
                                <span style={{ fontSize: 11, color: '#94A3B8', fontWeight: 500 }}>
                                    {currentStatus === 'connected' 
                                        ? (callType === 'video' ? `مكالمة فيديو • ${formatDuration(callSeconds)}` : `مكالمة صوتية • ${formatDuration(callSeconds)}`) 
                                        : (currentStatus === 'calling' ? 'جاري الاتصال...' : 'مكالمة واردة...')}
                                </span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            {currentStatus === 'connected' && callType === 'video' && (
                                <button style={styles.iconBtn} onClick={() => setIsFullscreen(!isFullscreen)} title={isFullscreen ? 'تصغير' : 'ملء الشاشة'}>
                                    {isFullscreen ? <Minimize size={18} color="#FFFFFF" /> : <Maximize size={18} color="#FFFFFF" />}
                                </button>
                            )}
                            <button style={styles.iconBtn} onClick={() => { sendSignal('END_CALL'); cleanupCall(); }} title="إغلاق">
                                <X size={18} color="#FFFFFF" />
                            </button>
                        </div>
                    </div>

                    {/* Dedicated Audio Element for reliable sound on mobile devices */}
                    <audio ref={remoteAudioRef} autoPlay playsInline />

                    {/* Video Streams (Only show if connected) */}
                    <div style={{ ...styles.videoContainer, display: currentStatus === 'connected' ? 'flex' : 'none' }}>
                        <video
                            key="remote-video"
                            ref={remoteVideoRef}
                            autoPlay
                            playsInline
                            onLoadedMetadata={(e) => e.target.play().catch(console.error)}
                            style={{
                                ...styles.remoteVideo,
                                opacity: 1,
                                position: callType === 'video' ? 'relative' : 'absolute',
                                pointerEvents: callType === 'video' ? 'auto' : 'none',
                                flex: 1,
                                zIndex: 1
                            }}
                        />
                        <video
                            key="local-video"
                            ref={localVideoRef}
                            autoPlay
                            playsInline
                            muted
                            onLoadedMetadata={(e) => e.target.play().catch(console.error)}
                            style={{
                                ...styles.localVideo,
                                opacity: 1,
                                visibility: callType === 'video' ? 'visible' : 'hidden',
                                pointerEvents: callType === 'video' ? 'auto' : 'none',
                                zIndex: 10
                            }}
                        />

                        {/* Persistent Audio View */}
                        <div key="audio-placeholder" style={{ ...styles.audioPlaceholder, display: callType === 'audio' ? 'flex' : 'none' }}>
                            <div style={styles.ambientGlow} />
                            {remoteAvatarUrl ? (
                                <img 
                                    src={resolveAvatarUrl(remoteAvatarUrl)} 
                                    alt={remoteName} 
                                    style={styles.avatarImg}
                                />
                            ) : (
                                <div style={styles.avatarPlaceholder}>
                                    {remoteName ? remoteName.charAt(0).toUpperCase() : 'L'}
                                </div>
                            )}
                            <h4 style={{ margin: '14px 0 4px 0', color: '#FFFFFF', fontSize: 18, fontWeight: 700 }}>
                                {remoteName || 'الدعم الفني'}
                            </h4>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10B981' }} />
                                <span style={{ fontSize: 13, color: '#38BDF8', fontWeight: 600 }}>
                                    {formatDuration(callSeconds)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Incoming Call Prompt View */}
                    <div key="prompt-receiving" style={{ ...styles.incomingPrompt, display: currentStatus === 'receiving' ? 'flex' : 'none' }}>
                        <div style={styles.avatarPulse}>
                            {remoteAvatarUrl ? (
                                <img 
                                    src={resolveAvatarUrl(remoteAvatarUrl)} 
                                    alt={remoteName} 
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        borderRadius: '50%',
                                        objectFit: 'cover'
                                    }} 
                                />
                            ) : (
                                <div style={{
                                    fontSize: 32,
                                    fontWeight: 'bold',
                                    color: '#FFFFFF',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #2563EB, #1D4ED8)'
                                }}>
                                    {remoteName ? remoteName.charAt(0).toUpperCase() : 'L'}
                                </div>
                            )}
                        </div>
                        <h4 style={{ margin: '18px 0 6px 0', fontSize: 18, color: '#FFFFFF', fontWeight: 700 }}>
                            {remoteName || 'الدعم الفني'}
                        </h4>
                        <p style={{ margin: '0 0 24px 0', fontSize: 13, color: '#94A3B8' }}>
                            {callType === 'video' ? 'مكالمة فيديو واردة...' : 'مكالمة صوتية واردة...'}
                        </p>
                        <div style={styles.actionRow}>
                            <button className="webrtc-ctrl-btn" style={styles.rejectBtn} onClick={rejectCall}>
                                <PhoneOff size={20} color="#FFFFFF" />
                                <span>رفض</span>
                            </button>
                            <button className="webrtc-ctrl-btn" style={styles.acceptBtn} onClick={acceptCall}>
                                <PhoneCall size={20} color="#FFFFFF" />
                                <span>قبول</span>
                            </button>
                        </div>
                    </div>

                    {/* Outgoing Call Prompt View */}
                    <div key="prompt-calling" style={{ ...styles.incomingPrompt, display: currentStatus === 'calling' ? 'flex' : 'none' }}>
                        <div style={styles.avatarPulse}>
                            <div style={{
                                fontSize: 32,
                                fontWeight: 'bold',
                                color: '#FFFFFF',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #3B82F6, #1D4ED8)'
                            }}>
                                {remoteName ? remoteName.charAt(0).toUpperCase() : 'L'}
                            </div>
                        </div>
                        <h4 style={{ margin: '18px 0 6px 0', fontSize: 18, color: '#FFFFFF', fontWeight: 700 }}>
                            {remoteName || 'الدعم الفني'}
                        </h4>
                        <p style={{ margin: '0 0 24px 0', fontSize: 13, color: '#60A5FA' }}>
                            جاري الاتصال...
                        </p>
                        <button className="webrtc-ctrl-btn" style={styles.cancelBtn} onClick={() => { sendSignal('END_CALL'); cleanupCall(); }}>
                            <PhoneOff size={20} color="#FFFFFF" />
                            <span>إلغاء الاتصال</span>
                        </button>
                    </div>

                    {/* Connected Controls (Visible, High-Contrast Floating Action Bar) */}
                    <div key="connected-controls" style={{ ...styles.controlsRow, display: currentStatus === 'connected' ? 'flex' : 'none' }}>
                        {/* Mic Mute / Unmute Button */}
                        <button 
                            className="webrtc-ctrl-btn" 
                            style={{
                                ...styles.controlBtn,
                                backgroundColor: isMuted ? 'rgba(239, 68, 68, 0.25)' : 'rgba(255, 255, 255, 0.12)',
                                border: isMuted ? '1.5px solid #EF4444' : '1px solid rgba(255, 255, 255, 0.2)'
                            }} 
                            onClick={toggleMute}
                            title={isMuted ? 'تشغيل المايكروفون' : 'كتم المايكروفون'}
                        >
                            {isMuted ? <MicOff size={22} color="#EF4444" /> : <Mic size={22} color="#FFFFFF" />}
                        </button>

                        {/* Video Camera On / Off Button */}
                        {callType === 'video' && (
                            <button 
                                className="webrtc-ctrl-btn" 
                                style={{
                                    ...styles.controlBtn,
                                    backgroundColor: isVideoOff ? 'rgba(239, 68, 68, 0.25)' : 'rgba(255, 255, 255, 0.12)',
                                    border: isVideoOff ? '1.5px solid #EF4444' : '1px solid rgba(255, 255, 255, 0.2)'
                                }} 
                                onClick={toggleVideo}
                                title={isVideoOff ? 'تشغيل الكاميرا' : 'إيقاف الكاميرا'}
                            >
                                {isVideoOff ? <CameraOff size={22} color="#EF4444" /> : <Camera size={22} color="#FFFFFF" />}
                            </button>
                        )}

                        {/* Switch Camera (Front/Back) Button */}
                        {callType === 'video' && (
                            <button 
                                className="webrtc-ctrl-btn" 
                                style={{
                                    ...styles.controlBtn,
                                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)'
                                }} 
                                onClick={switchCamera} 
                                title="تبديل الكاميرا"
                            >
                                <RefreshCcw size={22} color="#FFFFFF" />
                            </button>
                        )}

                        {/* End Call / Hangup Button */}
                        <button 
                            className="webrtc-ctrl-btn" 
                            style={styles.hangupBtn} 
                            onClick={() => { sendSignal('END_CALL'); cleanupCall(); }}
                            title="إنهاء المكالمة"
                        >
                            <PhoneOff size={24} color="#FFFFFF" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    overlay: {
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(2, 6, 23, 0.88)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        zIndex: 2000000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16
    },
    modal: {
        backgroundColor: '#0F172A',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        width: '100%',
        maxWidth: 380,
        borderRadius: 24,
        overflow: 'hidden',
        boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.95), 0 0 35px rgba(37, 99, 235, 0.15)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
    },
    header: {
        backgroundColor: '#0B0F19',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        padding: '14px 18px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    title: {
        color: '#F8FAFC',
        margin: 0,
        fontSize: 15,
        fontWeight: 700,
        letterSpacing: '0.2px'
    },
    iconBtn: {
        background: 'rgba(255, 255, 255, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        borderRadius: '50%',
        width: 34,
        height: 34,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        transition: 'all 0.2s'
    },
    incomingPrompt: {
        padding: '36px 24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        background: 'linear-gradient(180deg, #0F172A 0%, #020617 100%)'
    },
    avatarPulse: {
        width: 88,
        height: 88,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'webrtcPulse 2s infinite',
        border: '3px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 8px 24px rgba(37, 99, 235, 0.35)'
    },
    actionRow: {
        display: 'flex',
        gap: 16,
        width: '100%',
        justifyContent: 'center',
        marginTop: 6
    },
    rejectBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '12px 26px',
        borderRadius: 24,
        border: 'none',
        backgroundColor: '#DC2626',
        color: '#FFFFFF',
        cursor: 'pointer',
        fontWeight: 700,
        fontSize: 14,
        boxShadow: '0 4px 16px rgba(220, 38, 38, 0.4)'
    },
    acceptBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '12px 26px',
        borderRadius: 24,
        border: 'none',
        backgroundColor: '#16A34A',
        color: '#FFFFFF',
        cursor: 'pointer',
        fontWeight: 700,
        fontSize: 14,
        boxShadow: '0 4px 16px rgba(22, 163, 74, 0.4)'
    },
    cancelBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '12px 28px',
        borderRadius: 24,
        border: 'none',
        backgroundColor: '#DC2626',
        color: '#FFFFFF',
        cursor: 'pointer',
        fontWeight: 700,
        fontSize: 14,
        boxShadow: '0 4px 16px rgba(220, 38, 38, 0.4)'
    },
    videoContainer: {
        position: 'relative',
        width: '100%',
        aspectRatio: '3/4',
        backgroundColor: '#020617',
        overflow: 'hidden',
        flex: 1
    },
    remoteVideo: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    localVideo: {
        position: 'absolute',
        bottom: 14,
        right: 14,
        width: 90,
        height: 126,
        backgroundColor: '#0F172A',
        borderRadius: 14,
        objectFit: 'cover',
        boxShadow: '0 8px 24px rgba(0,0,0,0.7)',
        border: '2px solid rgba(255, 255, 255, 0.4)'
    },
    audioPlaceholder: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(180deg, #0F172A 0%, #020617 100%)',
        zIndex: 5
    },
    ambientGlow: {
        position: 'absolute',
        width: 140,
        height: 140,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(37, 99, 235, 0.25) 0%, rgba(37, 99, 235, 0) 70%)',
        filter: 'blur(20px)',
        zIndex: 1
    },
    avatarImg: {
        width: 88,
        height: 88,
        borderRadius: '50%',
        objectFit: 'cover',
        border: '3px solid rgba(255, 255, 255, 0.25)',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
        zIndex: 2
    },
    avatarPlaceholder: {
        width: 88,
        height: 88,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #1E293B, #0F172A)',
        border: '2px solid rgba(255, 255, 255, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 36,
        fontWeight: 'bold',
        color: '#FFFFFF',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
        zIndex: 2
    },
    controlsRow: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 16,
        padding: '16px 20px',
        backgroundColor: '#0B0F19',
        borderTop: '1px solid rgba(255, 255, 255, 0.08)'
    },
    controlBtn: {
        width: 50,
        height: 50,
        borderRadius: '50%',
        backgroundColor: 'rgba(255, 255, 255, 0.12)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: 0
    },
    hangupBtn: {
        width: 54,
        height: 54,
        borderRadius: '50%',
        backgroundColor: '#DC2626',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: '0 4px 18px rgba(220, 38, 38, 0.45)',
        padding: 0
    }
};

export default WebRTCCallOverlay;

